import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import { getBrandingBackgroundImage } from '@sonifi/wb-branding/services/branding.service';
import {
  getProductById,
  getProductsSettings,
  getSettingByProduct,
} from '@sonifi/wb-products/services/products.service';
import {
  checkUnpublishedChanges,
  setLoadingGuestRequestMenu,
  setMenu,
  setNodePreviewSelected,
  setNodeSelected,
  setPublishingStatus,
  setSaveItemInProgress,
  setServiceMenu,
  setShowModal,
  updateMenuLastUpdateByNodes,
} from '../../store/menu/guestRequestMenuSlice';
import { getInicialNode, getServiceMenuMapped } from './helpers/grMenuHelper';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { initConfirm } from '@sonifi/wb-notifier/services/confirm.service';
import escape from 'lodash/escape';
import {
  getJobsPublishStatus,
  processLatestPublishJob,
} from './grMenuJobs.service';
import {
  buildImagesParam,
  getBodyServiceIssues,
  getBodyServiceMenu,
  getServiceCategoryBody,
  getThumbnailBody,
} from './helpers/grMenuRequestsHelper';
import {
  processSaveItemNode,
  verifyMenuStructure,
} from './helpers/grMenuNodeValidations';
import { ALERTS_IDS } from '@sonifi/wb-notifier/constants/notifierConstants';
import {
  getIdFromUrl,
  parseQueryParams,
} from '@sonifi/wb-core/utils/parseHelper';
import {
  buildWbTreeByNode,
  addWarnings,
  addNodeToWbTree,
  removeNodeFromWbTree,
  findNodeParent,
  findNodeById,
  findNodeByUri,
} from '@sonifi/wb-tree/utils/helper';
import uniqueId from 'lodash/uniqueId';
import { GR_NODE_TYPES } from '../../constants/wbGuestRequestConstants';
import { GA_ACTIONS, GA_LABELS, sendEventGA } from '@sonifi/wb-core/services/ga.service';

const getServiceCenterSettings = async (groupUri) => {
  try {
    const res = await apiService.get(
      `${ENDPOINTS.SERVICE_CENTER_SETTINGS}?group=${groupUri}`,
    );
    const serviceCenterSettings = res.data.objects?.length
      ? res.data.objects[0]
      : null;
    return serviceCenterSettings;
  } catch (error) {
    return Promise.reject(error);
  }
};

const getMediaDependencies = async ({ group, imgData }) => {
  try {
    const params = {
      group: group.uri,
      id__in: buildImagesParam(imgData),
      rpp: -1,
    };
    const url = `${ENDPOINTS.IMAGES}?${parseQueryParams(params)}`;
    const res = await apiService.get(url);
    return res.data.objects;
  } catch (error) {
    return [];
  }
};

const getServiceMenu = async (group) => {
  try {
    const url = `${ENDPOINTS.SERVICE_MENUS}?group=${group.uri}`;
    const res = await apiService.get(url);
    const mediaDependencies = await getMediaDependencies({
      group,
      imgData: res.data._embedded,
    });

    const serviceMenu = getServiceMenuMapped({
      data: res.data,
      group,
      mediaDependencies,
    });
    return serviceMenu;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const initGuestRequestMenu =
  ({ groupId, id, platform }) =>
  async (dispatch, getState) => {
    dispatch(setLoadingGuestRequestMenu(LOADING_STATUS.loading));

    try {
      const {
        groups: { selectedGroup },
      } = getState();
      const productsSettings = await dispatch(getProductsSettings(groupId));
      const grProductSettings = dispatch(
        getSettingByProduct({
          groupId,
          productKey: PRODUCTS_KEYS.GUEST_REQUEST,
        }),
      );
      const background = dispatch(
        getBrandingBackgroundImage({ groupId, productsSettings }),
      );
      const serviceCenterSettings = await getServiceCenterSettings(
        selectedGroup.uri,
      );
      const guestRequest = dispatch(
        getProductById(PRODUCTS_KEYS.GUEST_REQUEST),
      );
      const serviceMenu = await getServiceMenu(selectedGroup);
      const { lastPublishJob, lastPublished, lastPublishUser } = await dispatch(
        getJobsPublishStatus({ serviceMenuUri: serviceMenu.uri }),
      );

      const node = getInicialNode({ id, menu: serviceMenu.menu });

      await Promise.all([
        dispatch(
          setMenu({
            background,
            guestRequest,
            lastPublished,
            lastPublishUser,
            lastPublishJob,
            platform,
            productSettings: grProductSettings,
            serviceCenterSettings,
            serviceMenu,
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(setLoadingGuestRequestMenu(LOADING_STATUS.success)),
        dispatch(canPublishMenu({ superStrict: false })),
      ]);
      return node.id;
    } catch (error) {
      console.error(error);
      dispatch(setLoadingGuestRequestMenu(LOADING_STATUS.error));
    }
  };

export const toggleNodeActive = (node) => async (dispatch, getState) => {
  try {
    const {
      guestRequestMenu: { nodePreviewSelected, serviceMenu },
    } = getState();

    // Update Store first
    const serviceMenuUpdated = buildWbTreeByNode({ wbTree: serviceMenu, node });

    const bodyServiceMenu = getBodyServiceMenu({
      serviceMenu: serviceMenuUpdated,
    });

    const resMenu = await apiService.patch(
      `${ENDPOINTS.SERVICE_MENUS}/${serviceMenu.id}`,
      bodyServiceMenu,
    );
    const nodePreviewSelectedUpdated = findNodeById({
      menu: serviceMenuUpdated.menu,
      nodeId: nodePreviewSelected.id,
    });

    await Promise.all([
      dispatch(
        setServiceMenu({
          serviceMenu: {
            ...serviceMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreviewSelectedUpdated })),
      dispatch(checkUnpublishedChanges()),
    ]);
  } catch (error) {
    console.error(error);
  }
};

export const saveGuestRequestItemNode =
  (nodeData) => async (dispatch, getState) => {
    try {
      dispatch(setSaveItemInProgress(true));

      const {
        groups: { selectedGroup },
        guestRequestMenu: {
          addNodeFrom,
          isOrderByOptions,
          productSettings,
          serviceCenterSettings,
          serviceMenu,
        },
      } = getState();

      await processSaveItemNode({
        group: selectedGroup,
        isOrderByOptions,
        node: nodeData,
        productSettings,
        serviceCenterSettings,
      });

      let serviceMenuUpdated = {};
      let node = nodeData;

      const bodyServiceIssues = getBodyServiceIssues({
        group: selectedGroup,
        node: nodeData,
      });

      if (nodeData.subject.id) {
        const res = await apiService.patch(
          `${ENDPOINTS.SERVICE_ISSUES}/${nodeData.subject.id}`,
          bodyServiceIssues,
        );

        node = {
          ...node,
          subject: {
            ...node.subject,
            ...res.data,
          },
          indicatorImage: {
            showIcon: res.data?.options?.length > 0,
            showTooltip: false,
          },
        };

        // Update Tree first
        serviceMenuUpdated = buildWbTreeByNode({
          wbTree: serviceMenu,
          node,
        });
      } else {
        const res = await apiService.post(
          ENDPOINTS.SERVICE_ISSUES,
          bodyServiceIssues,
        );

        node = {
          ...node,
          id: uniqueId(`${Date.now()}_`),
          active: true,
          subject: {
            ...node.subject,
            ...res.data,
          },
          indicatorImage: {
            showIcon: res.data?.options?.length > 0,
            showTooltip: false,
          },
          secondaryValue: '',
        };
        // Update Tree first
        serviceMenuUpdated = addNodeToWbTree({
          wbTree: serviceMenu,
          node,
          addNodeFrom,
        });
      }

      const bodyServiceMenu = getBodyServiceMenu({
        serviceMenu: serviceMenuUpdated,
      });
      const resMenu = await apiService.patch(
        `${ENDPOINTS.SERVICE_MENUS}/${serviceMenu.id}`,
        bodyServiceMenu,
      );

      await Promise.all([
        dispatch(
          setServiceMenu({
            serviceMenu: {
              ...serviceMenuUpdated,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(setSaveItemInProgress(false)),
        dispatch(checkUnpublishedChanges()),
        dispatch(setShowModal(false)),
      ]);
      return node;
    } catch (error) {
      console.error(error);
      dispatch(setSaveItemInProgress(false));

      if (error && Array.isArray(error.response?.data)) {
        const errors = error.response.data.map(
          (err) => `<li>${err.message}</li>`,
        );
        dispatch(
          setAlert({
            id: ALERTS_IDS.grItemNode,
            title: 'Settings save failed',
            content: `The settings for this property could not be saved. Error(s) returned by server: <ul>${errors}</ul>`,
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      } else {
        dispatch(
          setAlert({
            id: ALERTS_IDS.grItemNode,
            title: 'Settings save failed',
            content: 'The settings for this property could not be saved.',
            type: 'danger',
            show: true,
            dismissable: true,
          }),
        );
      }
      return null;
    }
  };

export const saveGuestRequestCategoryNode =
  ({ nodeData, keepModalOpen }) =>
  async (dispatch, getState) => {
    try {
      dispatch(setSaveItemInProgress(true));
      const {
        groups: { selectedGroup },
        guestRequestMenu: { addNodeFrom, serviceMenu },
      } = getState();

      let imageUrl = null;
      if (nodeData.subject.image?.length) {
        const thumbnailBody = getThumbnailBody({ node: nodeData });
        const resThumbnail = await apiService.post(
          ENDPOINTS.THUMBNAILS,
          thumbnailBody,
        );
        imageUrl = resThumbnail.data.signed_url;
        nodeData = {
          ...nodeData,
          subject: {
            ...nodeData.subject,
            image: [{ ...nodeData.subject.image[0], display_uri: imageUrl }],
          },
        };
      }
      const serviceCategoriesBody = getServiceCategoryBody({
        node: nodeData,
        groupUri: selectedGroup.uri,
        imageUrl,
      });

      let serviceMenuUpdated = null;
      let node = nodeData;
      if (nodeData.subject.id) {
        const res = await apiService.patch(
          `${ENDPOINTS.SERVICE_CATEGORIES}/${nodeData.subject.id}`,
          serviceCategoriesBody,
        );

        node = {
          ...node,
          subject: {
            ...nodeData.subject,
            last_updated: res.data.last_updated,
          },
          valid_child_types: node.valid_child_types || [
            GR_NODE_TYPES.serviceIssues,
          ],
        };
        // Update Tree first
        serviceMenuUpdated = buildWbTreeByNode({
          wbTree: serviceMenu,
          node,
        });
      } else {
        const res = await apiService.post(
          ENDPOINTS.SERVICE_CATEGORIES,
          serviceCategoriesBody,
        );
        node = {
          ...node,
          id: res.data.id,
          active: true,
          subject: {
            ...node.subject,
            ...res.data,
          },
          valid_child_types: node.valid_child_types || [
            GR_NODE_TYPES.serviceIssues,
          ],
        };
        // Update Tree first
        serviceMenuUpdated = addNodeToWbTree({
          wbTree: serviceMenu,
          node,
          addNodeFrom,
        });
      }

      const bodyServiceMenu = getBodyServiceMenu({
        serviceMenu: serviceMenuUpdated,
      });
      const resMenu = await apiService.patch(
        `${ENDPOINTS.SERVICE_MENUS}/${serviceMenu.id}`,
        bodyServiceMenu,
      );

      await Promise.all([
        dispatch(
          setServiceMenu({
            serviceMenu: {
              ...serviceMenuUpdated,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(setSaveItemInProgress(false)),
        dispatch(checkUnpublishedChanges()),
        !keepModalOpen && dispatch(setShowModal(false)),
      ]);
      return node;
    } catch (error) {
      console.error(error);
      dispatch(setSaveItemInProgress(false));
      return null;
    }
  };

export const getImageAndUpdateNode = (node) => async (dispatch) => {
  try {
    const imageUrl = node.subject.image?.length
      ? node.subject.image[0].image
      : null;
    if (!imageUrl) {
      return false;
    }
    const imageId = getIdFromUrl({ url: imageUrl, parseToInt: false });
    const res = await apiService.get(`${ENDPOINTS.IMAGES}/${imageId}`);
    const fileUri = res.data._embedded[res.data.files[0]].file_uri;
    const nodeWithImage = {
      ...node,
      subject: {
        ...node.subject,
        image: [
          {
            ...node.subject.image[0],
            file_uri: fileUri,
            _embedded: res.data._embedded[res.data.files[0]],
          },
        ],
      },
    };
    dispatch(setNodeSelected({ node: nodeWithImage }));
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const removeNode = (node) => async (dispatch, getState) => {
  try {
    const {
      guestRequestMenu: { serviceMenu },
    } = getState();

    const removeNodeFrom = findNodeParent({ menu: serviceMenu.menu, node });
    const serviceMenuUpdated = removeNodeFromWbTree({
      wbTree: serviceMenu,
      node,
      removeNodeFrom,
    });

    const bodyServiceMenu = getBodyServiceMenu({
      serviceMenu: serviceMenuUpdated,
    });
    const resMenu = await apiService.patch(
      `${ENDPOINTS.SERVICE_MENUS}/${serviceMenu.id}`,
      bodyServiceMenu,
    );

    const nodePreview = getInicialNode({
      id: removeNodeFrom.id,
      menu: serviceMenuUpdated.menu,
    });

    await Promise.all([
      dispatch(
        setServiceMenu({
          serviceMenu: {
            ...serviceMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreview })),
      dispatch(checkUnpublishedChanges()),
    ]);
    return nodePreview;
  } catch (error) {
    console.error(error);
    const {
      guestRequestMenu: { serviceMenu: serviceMenuOld },
    } = getState();
    dispatch(
      setAlert({
        title: 'Settings save failed',
        content: 'The settings for this property could not be saved.',
        type: 'danger',
        show: true,
        dismissable: true,
      }),
    );
    // Restore previous state
    dispatch(
      setServiceMenu({
        serviceMenu: { ...serviceMenuOld },
      }),
    );
  }
};

export const patchGRserviceMenu =
  (serviceMenu) => async (dispatch, getState) => {
    try {
      const {
        guestRequestMenu: { nodePreviewSelected },
      } = getState();
      const bodyServiceMenu = getBodyServiceMenu({
        serviceMenu,
      });

      const resMenu = await apiService.patch(
        `${ENDPOINTS.SERVICE_MENUS}/${serviceMenu.id}`,
        bodyServiceMenu,
      );

      const nodePreviewUpdated = findNodeByUri({
        menu: serviceMenu.menu,
        nodeUri: nodePreviewSelected.subject.uri,
      });

      await Promise.all([
        dispatch(
          setServiceMenu({
            serviceMenu: {
              ...serviceMenu,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(setNodePreviewSelected({ node: nodePreviewUpdated })),
        dispatch(checkUnpublishedChanges()),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

const canPublishMenu =
  ({ superStrict }) =>
  (dispatch) => {
    const { validationResults, nodes, lastUpdated } = dispatch(
      verifyMenuStructure({ superStrict }),
    );
    dispatch(
      updateMenuLastUpdateByNodes({
        nodes,
        lastUpdated,
      }),
    );

    let canPublish = true;
    if (validationResults.errors.length) {
      let message =
        'The selected menu cannot be published until the following issues are fixed.<ul>';

      for (const msg of validationResults.errors) {
        message += `<li>${escape(msg)}</li>`;
      }
      message += '</ul>';

      message += addWarnings(validationResults);
      dispatch(
        setAlert({
          content: message,
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
      canPublish = false;
    }

    return {
      canPublish,
      validationResults,
    };
  };

export const publishGuestRequest = () => async (dispatch, getState) => {
  try {
    const {
      guestRequestMenu: { serviceMenu },
    } = getState();
    const { canPublish, validationResults } = dispatch(
      canPublishMenu({ superStrict: true }),
    );

    if (canPublish) {
      let message = 'Are you sure you want to publish the selected menu?';
      message += addWarnings(validationResults);
      const isConfirmed = await dispatch(
        initConfirm({
          title: message,
        }),
      );
      if (!isConfirmed) {
        return false;
      }

      const timestamp = Math.round(new Date().getTime() / 1000); // FIXME: Make this equal to the latest seen piece of content in the menu
      const body = {
        targets: [`${serviceMenu.uri}/as_of/${timestamp}`],
        type: 'PUBLISH_SERVICE_MENU',
      };

      const res = await apiService.post(ENDPOINTS.JOBS, body);
      dispatch(setPublishingStatus({ job: res.data }));
      await dispatch(
        processLatestPublishJob({
          job: res.data,
          serviceMenuUri: serviceMenu.uri,
        }),
      );
      sendEventGA({ action: GA_ACTIONS.publish, label: GA_LABELS.guestRequest });
    }
  } catch (error) {
    console.error(error);
  }
};
